import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { fetchEndpointResult } from 'api';
import { useEffectOnce, useGlobalLinks, useRequiredContext } from 'hooks';
import { usePath } from 'hooks/navigation';
import type { Cart, MinimizedAddToCartResponse } from 'models/api';
import { ignorePromiseRejection } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import { useToastContext } from './ToastContextProvider';

interface CartContextType {
	addedProductPopoverData: MinimizedAddToCartResponse | undefined;
	cart: Cart | undefined;
	closeAddedProductPopover: () => void;
	isInitialLoading: boolean;
	loadFullCart: () => Promise<void>;
	openAddedProductPopover: (miniCart: MinimizedAddToCartResponse) => void;
	quantity: number | undefined;
	updateCart: (cart: Cart) => void;
	updateTotalQuantity: (quantity: number) => void;
}

export const CartContext = createContext<CartContextType | undefined>(
	undefined,
);

interface Props {
	children: ReactNode;
}

export function CartContextProvider({ children }: Props) {
	const { t } = useI18n();
	const { cart: cartPage } = useGlobalLinks();
	const path = usePath();
	const { sendToast } = useToastContext();
	const [quantity, setQuantity] = useState<number>();
	const [cart, setCart] = useState<Cart>();
	const [addedProductPopoverData, setAddedProductPopoverData] = useState<
		MinimizedAddToCartResponse | undefined
	>();

	//TODO: Do we need a cart loading error state for the cart page?
	const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

	const loadFullCart = useCallback(async () => {
		const result = await fetchEndpointResult('PostCart');
		if (result.isFailure()) {
			sendToast({
				message: t('general_critical_error_text'),
				variant: 'error',
			});
			return;
		}
		setIsInitialLoading(false);
		if (result.value) {
			setCart(result.value);
			setQuantity(result.value.totalQty);
		}
	}, [sendToast, t]);

	const loadMiniCart = useCallback(async () => {
		const result = await fetchEndpointResult('GetCartMini');
		if (result.isFailure()) {
			sendToast({
				message: t('general_critical_error_text'),
				variant: 'error',
			});
			return;
		}
		setIsInitialLoading(false);
		if (result.value) {
			setQuantity(result.value.totalQty);
		}
	}, [sendToast, t]);

	useEffectOnce(() => {
		if (path.startsWith(cartPage)) {
			ignorePromiseRejection(loadFullCart());
		} else {
			ignorePromiseRejection(loadMiniCart());
		}
	});

	const updateTotalQuantity = useCallback((newQuantity: number) => {
		setQuantity(newQuantity);
	}, []);

	const updateCart = useCallback((updatedCart: Cart) => {
		setCart(updatedCart);
	}, []);

	const openAddedProductPopover = useCallback(
		(miniCart: MinimizedAddToCartResponse) => {
			setAddedProductPopoverData(miniCart);
		},
		[],
	);
	const closeAddedProductPopover = useCallback(() => {
		setAddedProductPopoverData(undefined);
	}, []);

	const value = useMemo(
		() => ({
			addedProductPopoverData,
			cart,
			closeAddedProductPopover,
			loadFullCart,
			isInitialLoading,
			openAddedProductPopover,
			quantity,
			updateCart,
			updateTotalQuantity,
		}),
		[
			addedProductPopoverData,
			cart,
			closeAddedProductPopover,
			loadFullCart,
			isInitialLoading,
			openAddedProductPopover,
			quantity,
			updateCart,
			updateTotalQuantity,
		],
	);

	return <CartContext value={value}>{children}</CartContext>;
}

CartContextProvider.displayName = 'CartContextProvider';

export function useCartContext() {
	return useRequiredContext(CartContext, 'CartContext');
}
